import { useCallback, useState } from 'react';

import { isWithinSafeArea } from '@/playlist/games/letterParty/Helpers';

import type { Question } from '@/playlist/types';

interface Position {
	x: number;
	y: number;
}

interface LetterQ {
	position: Position;
	letter: string;
	collected: boolean;
	colorSeed: number;
}

interface HatCollectible {
	position: Position;
	collected: boolean;
	id: number;
}

interface ConfettiCollectible {
	position: Position;
	collected: boolean;
	id: number;
}

type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';

interface UseQuestionSetupProps {
	questions: Question[];
	snake: Position[];
	directionRef: React.MutableRefObject<Direction>;
	nextDirectionRef: React.MutableRefObject<Direction>;
	gridWidth: number;
	gridHeight: number;
	safeAreaWidth: number;
	safeAreaHeight: number;
	onResetSnake: () => void;
	onResetGameState: () => void;
	onResetWordProgress: () => void;
	onPauseSnake: () => void;
}

interface UseQuestionSetupReturn {
	currentWord: string;
	currentQuestionIndex: number;
	letterQs: LetterQ[];
	hats: HatCollectible[];
	confettis: ConfettiCollectible[];
	setLetterQs: React.Dispatch<React.SetStateAction<LetterQ[]>>;
	setHats: React.Dispatch<React.SetStateAction<HatCollectible[]>>;
	setConfettis: React.Dispatch<React.SetStateAction<ConfettiCollectible[]>>;
	startNewWord: (preserveScore?: boolean, preserveSnake?: boolean) => void;
	initGame: () => void;
	isLastQuestion: boolean;
}

const CELL_DISTANCE_BUFFER = 2; // Minimum distance between letters

/**
 * Custom hook to manage question setup for Letter Party game
 *
 * Handles:
 * - Question tracking and word extraction
 * - Correct letter placement from word
 * - Decoy letter placement from backend response
 * - Hat collectible placement
 * - Safe area management (avoiding snake and reserved zones)
 * - Game progression through questions
 */
export function useQuestionSetup({
	questions,
	snake,
	directionRef,
	nextDirectionRef,
	gridWidth,
	gridHeight,
	safeAreaWidth,
	safeAreaHeight,
	onResetSnake,
	onResetGameState,
	onResetWordProgress,
	onPauseSnake,
}: UseQuestionSetupProps): UseQuestionSetupReturn {
	const [currentWord, setCurrentWord] = useState('');
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [letterQs, setLetterQs] = useState<LetterQ[]>([]);
	const [hats, setHats] = useState<HatCollectible[]>([]);
	const [confettis, setConfettis] = useState<ConfettiCollectible[]>([]);

	/**
	 * Calculate safe areas where collectibles should not spawn
	 */
	const calculateSafeAreas = useCallback(
		(preserveSnake: boolean): Set<string> => {
			const usedPositions = new Set<string>();

			// Reserve the safe area in the top-left corner
			for (let x = 0; x < safeAreaWidth; x++) {
				for (let y = 0; y < safeAreaHeight; y++) {
					usedPositions.add(`${x},${y}`);
				}
			}

			// When continuing with the current snake, reserve area around its head
			// 2 cells on top, 2 cells on right, 1 cell on bottom from leader
			if (preserveSnake && snake.length > 0) {
				const leaderX = snake[0].x;
				const leaderY = snake[0].y;

				// 2 cells above leader
				for (let dy = -CELL_DISTANCE_BUFFER; dy <= 0; dy++) {
					usedPositions.add(`${leaderX},${leaderY + dy}`);
				}

				// 2 cells to the right of leader
				for (let dx = 0; dx <= CELL_DISTANCE_BUFFER; dx++) {
					usedPositions.add(`${leaderX + dx},${leaderY}`);
				}

				// 2 cells below leader
				for (let dy = 0; dy <= CELL_DISTANCE_BUFFER; dy++) {
					usedPositions.add(`${leaderX},${leaderY + dy}`);
				}
			}

			// Block all current snake positions
			snake.forEach((seg) => {
				usedPositions.add(`${seg.x},${seg.y}`);
			});

			return usedPositions;
		},
		[snake, safeAreaWidth, safeAreaHeight],
	);

	/**
	 * Check if a position is too close to already placed letters
	 */
	const isTooCloseToOtherLetters = useCallback(
		(x: number, y: number, placedLetters: LetterQ[]): boolean => {
			for (const letterQ of placedLetters) {
				const dx = Math.abs(letterQ.position.x - x);
				const dy = Math.abs(letterQ.position.y - y);

				// Check if within minimum distance (Manhattan distance)
				if (dx + dy < CELL_DISTANCE_BUFFER) {
					return true;
				}
			}

			return false;
		},
		[],
	);

	/**
	 * Place correct letters from the word on the grid
	 */
	const placeCorrectLetters = useCallback(
		(word: string, usedPositions: Set<string>): LetterQ[] => {
			const letters = word.toLowerCase().split('');
			const positions: LetterQ[] = [];

			letters.forEach((letter) => {
				let x: number = 0;
				let y: number = 0;
				let attempts = 0;

				do {
					x = Math.floor(Math.random() * gridWidth);
					y = Math.floor(Math.random() * gridHeight);
					attempts++;
					if (attempts > 100) return; // Give up if can't find spot
				} while (
					usedPositions.has(`${x},${y}`) ||
					isWithinSafeArea({ x, y }) ||
					isTooCloseToOtherLetters(x, y, positions)
				);

				usedPositions.add(`${x},${y}`);
				positions.push({
					position: { x, y },
					letter,
					collected: false,
					colorSeed: x * 100 + y,
				});
			});

			return positions;
		},
		[gridWidth, gridHeight, isTooCloseToOtherLetters],
	);

	/**
	 * Place decoy letters from backend response
	 */
	const placeDecoyLetters = useCallback(
		(
			usedPositions: Set<string>,
			backendDecoyLetters?: string[],
			existingLetters?: LetterQ[],
		): LetterQ[] => {
			if (!backendDecoyLetters || backendDecoyLetters.length === 0) {
				return [];
			}

			const decoyLetters = backendDecoyLetters.map((letter) => letter.toLowerCase());
			const positions: LetterQ[] = [];
			const allPlacedLetters = [...(existingLetters || [])];

			// Place decoy letters
			decoyLetters.forEach((letter) => {
				let x: number = 0;
				let y: number = 0;
				let attempts = 0;

				do {
					x = Math.floor(Math.random() * gridWidth);
					y = Math.floor(Math.random() * gridHeight);
					attempts++;
					if (attempts > 100) return; // Give up if can't find spot
				} while (
					usedPositions.has(`${x},${y}`) ||
					isWithinSafeArea({ x, y }) ||
					isTooCloseToOtherLetters(x, y, allPlacedLetters)
				);

				usedPositions.add(`${x},${y}`);
				const newLetter = {
					position: { x, y },
					letter,
					collected: false,
					colorSeed: x * 100 + y,
				};
				positions.push(newLetter);
				allPlacedLetters.push(newLetter);
			});

			return positions;
		},
		[gridWidth, gridHeight, isTooCloseToOtherLetters],
	);

	/**
	 * Place hat collectibles on the grid
	 */
	const placeHats = useCallback(
		(usedPositions: Set<string>): HatCollectible[] => {
			// Place 1-2 hats randomly per word
			const numHats = 1 + Math.floor(Math.random() * 2);
			const hatPositions: HatCollectible[] = [];

			for (let i = 0; i < numHats; i++) {
				let x: number = 0;
				let y: number = 0;

				do {
					x = Math.floor(Math.random() * gridWidth);
					y = Math.floor(Math.random() * gridHeight);
				} while (usedPositions.has(`${x},${y}`) || isWithinSafeArea({ x, y }));

				usedPositions.add(`${x},${y}`);
				hatPositions.push({
					position: { x, y },
					collected: false,
					id: i,
				});
			}

			return hatPositions;
		},
		[gridWidth, gridHeight],
	);

	/**
	 * Place confetti collectibles on the grid
	 */
	const placeConfettis = useCallback(
		(usedPositions: Set<string>): ConfettiCollectible[] => {
			const confettiPositions: ConfettiCollectible[] = [];
			let x: number = 0;
			let y: number = 0;

			do {
				x = Math.floor(Math.random() * gridWidth);
				y = Math.floor(Math.random() * gridHeight);
			} while (usedPositions.has(`${x},${y}`) || isWithinSafeArea({ x, y }));

			usedPositions.add(`${x},${y}`);
			confettiPositions.push({
				position: { x, y },
				collected: false,
				id: 0,
			});

			return confettiPositions;
		},
		[gridWidth, gridHeight],
	);

	/**
	 * Start a new word/question
	 */
	const startNewWord = useCallback(
		(preserveScore: boolean = false, preserveSnake: boolean = false) => {
			// Get current question from questions array
			const questionIndex = preserveScore ? currentQuestionIndex + 1 : 0;
			const currentQuestion = questions[questionIndex];

			// If no question available, handle gracefully
			if (!currentQuestion) {
				console.warn('Letter Party - No question available at index:', questionIndex);
				return;
			}

			// Get the word from question_display and validate
			let word = '';
			if (typeof currentQuestion.question_display === 'string') {
				word = currentQuestion.question_display.trim().toLowerCase();
			} else {
				console.warn(
					'Letter Party - question_display is not a string:',
					currentQuestion.question_display,
				);
				return;
			}

			setCurrentWord(word);

			// Always reset word progress (letters collected/revealed) for new word
			onResetWordProgress();

			// Pause snake when starting next question (wait for player input)
			if (preserveScore && preserveSnake) {
				onPauseSnake();
			}

			// Reset snake position and direction if not preserving
			if (!preserveSnake) {
				onResetSnake();
				directionRef.current = 'RIGHT';
				nextDirectionRef.current = 'RIGHT';
			}

			// Calculate safe areas for placement
			const usedPositions = calculateSafeAreas(preserveSnake);

			// Place all collectibles
			const correctLetters = placeCorrectLetters(word, usedPositions);
			const decoyLetters = placeDecoyLetters(
				usedPositions,
				currentQuestion.decoy_letters,
				correctLetters,
			);
			const hatPositions = placeHats(usedPositions);
			const confettiPositions = placeConfettis(usedPositions);

			setLetterQs([...correctLetters, ...decoyLetters]);
			setHats(hatPositions);
			setConfettis(confettiPositions);

			// Update question index
			setCurrentQuestionIndex(questionIndex);

			// Reset game state if starting fresh
			if (!preserveScore) {
				onResetGameState();
			}
		},
		[
			currentQuestionIndex,
			questions,
			onResetSnake,
			onResetGameState,
			onResetWordProgress,
			onPauseSnake,
			directionRef,
			nextDirectionRef,
			calculateSafeAreas,
			placeCorrectLetters,
			placeDecoyLetters,
			placeHats,
			placeConfettis,
		],
	);

	/**
	 * Initialize game (resets to first question)
	 */
	const initGame = useCallback(() => {
		startNewWord(false, false);
	}, [startNewWord]);

	const isLastQuestion = currentQuestionIndex >= questions.length - 1;

	return {
		currentWord,
		currentQuestionIndex,
		letterQs,
		hats,
		confettis,
		setLetterQs,
		setHats,
		setConfettis,
		startNewWord,
		initGame,
		isLastQuestion,
	};
}
